<template>
    <div class="lms__superadmin_workspaces_edit_selfregistration_edit_form'">
        <form-text
            :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_keyform_name'"
            :name="'lms-superadmin-workspace-edit-selfregistration-name'"
            :label="'Key name'"
            :required="true"
            :v-validate="{ required: true }"
            :data-vv-as="'key name'"
            v-model="keyName"
        >{{ isAddMode ? 'Give the key a name so it is easier to find later.' : '' }}
        </form-text>
        <form-text
            v-if="isEditMode"
            :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_keyform_key'"
            :name="'lms-superadmin-workspace-edit-selfregistration-key'"
            :label="'Key'"
            :read-only="true"
            :icon="true"
            :icon-name="'copy'"
            :icon-copyable="true"
            :icon-copy-name="'key'"
            :value="keyCode"
        ></form-text>
        <form-text
            v-if="isEditMode"
            :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_keyform_link'"
            :name="'lms-superadmin-workspace-edit-selfregistration-link'"
            :label="'Link'"
            :read-only="true"
            :icon="true"
            :icon-name="'link'"
            :icon-copyable="true"
            :icon-copy-name="'link'"
            :value="keyLink"
        ></form-text>
        <form-number
            :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_keyform_limit'"
            :name="'lms-superadmin-workspace-edit-selfregistration-limit'"
            :label="isAddMode ? `Number of uses allowed` : `Usage limit <span>(${keyUsed} used)</span>`"
            :label-html="true"
            :required="true"
            :v-validate="{ required: true, min_value: keyUsed || 1, max_value: 10000 }"
            :data-vv-as="'usage limit'"
            v-model="keyLimit"
        >{{ isAddMode ? 'A self-registration key can be used a maximum of 10,000 times.' : 'You can only increase the usage limit. A self-registration key can be used a maximum of 10,000 times.' }}
        </form-number>
        <component-title :size="'medium'">Limitations</component-title>
        <form-date
            :label="'Expiry date'"
            :name="'lms-superadmin-workspace-edit-selfregistration-expiry-date'"
            :v-validate="{ date_format: 'yyyy-MM-dd', after: todaysDate }"
            :v-validate-custom-error="`The expiry date must be after ${todaysDate}`"
            v-model="keyExpiryDate"
        >Enter an expiry date, when the key will be deactivated.</form-date>
        <component-title :size="'medium'">Self-registration options</component-title>
        <component-wrapper :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_orgunits'">
            <form-radio-horizontal
                :disabled="!orgUnitsLength"
                :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_orgunits_radio'"
                :label="'Organisation units'"
                :required="true"
                :items="[
                {
                    name: 'All',
                    value: 'all'
                },
                {
                    name: 'Specific',
                    value: 'specific'
                }
            ]"
                :name="'lms-superadmin-workspace-edit-selfregistration-organisation-units-available-for'"
                :v-validate="'required'"
                :data-vv-as="'organisation-units'"
                :hint-position-top="true"
                v-model="keyOrganisationUnitsAvailableFor"
            >Choose organisation units that a learner can select when using this key.</form-radio-horizontal>
            <component-note v-if="!orgUnitsLength">There are no organisation units in this workspace.</component-note>
            <form-select-org-units
                v-show="orgUnitsLength"
                ref="selectorgunits"
                :label="''"
                :name="'lms-superadmin-workspace-edit-selfregistration-organisation-units-list'"
                :disabled="keyOrganisationUnitsAvailableFor !== 'specific'"
                :workspace-id="originWorkspaceId"
                :v-validate="{ required: keyOrganisationUnitsAvailableFor === 'specific' }"
                :data-vv-as="'org units'"
                :value="keyOrganisationUnitsList"
                :placeholder="keyOrganisationUnitsAvailableFor === 'all' ? 'All units' : ''"
                @input="setKeyOrganisationUnitsList"
            ></form-select-org-units>
        </component-wrapper>
        <component-wrapper :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_groups'">
            <form-radio-horizontal
                :disabled="!orgUnitsLength"
                :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_groups_radio'"
                :label="'Groups'"
                :items="[
                    {
                        name: 'All',
                        value: 'all'
                    },
                    {
                        name: 'None',
                        value: 'none'
                    },
                    {
                        name: 'Specific',
                        value: 'specific'
                    }
                ]"
                :name="'lms-superadmin-workspace-edit-selfregistration-form-groups-available-for'"
                :data-vv-as="'groups'"
                :hint-position-top="true"
                v-model="keyGroupsAvailableFor"
            >Choose groups that a learner can select when using this key.</form-radio-horizontal>
            <component-note v-if="!groupsLength">There are no groups in this workspace.</component-note>
            <form-select
                v-show="groupsLength"
                :multiple="true"
                :disabled="keyGroupsAvailableFor !== 'specific'"
                :label="''"
                :get-items-getter="'getLmsSuperadminGroupsMappedIdName'"
                :get-items-getter-mapped="'getLmsSuperadminGroupsMappedLabelValue'"
                :get-items-dispatch="'lmsSuperadminGroupsGet'"
                :get-items-dispatch-data="originWorkspaceId"
                :advanced-search-active="true"
                :v-validate="keyGroupsAvailableFor === 'specific' ? { required: true } : ''"
                :name="'lms-superadmin-workspace-edit-selfregistration-groups-items'"
                :data-vv-as="'groups'"
                v-model="keyGroupsList"
            ></form-select>
        </component-wrapper>
        <form-checkbox
            :class="'lms__superadmin_workspaces_edit_selfregistration_edit_form_keyform_status'"
            :name="'lms-superadmin-workspace-edit-selfregistration-status'"
            :label="'Key status'"
            :switcher="true"
            :value="keyStatus"
            @input="setKeyStatus"
        >When this code is activated, new users will be able to self register as a learner to this workspace.
        </form-checkbox>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import FormText from '@/components/default/forms/FormText'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormNumber from '@/components/default/forms/FormNumber'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormDate from '@/components/default/forms/FormDate'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormSelect from '@/components/default/forms/FormSelect'
import { toISODateUTC } from '@/functions'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import ComponentNote from '@/components/default/shared/ComponentNote'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'

export default {
  name: 'lms-superadmin-workspace-edit-selfregistration-edit-form',
  components: { ComponentWrapper, ComponentNote, FormSelectOrgUnits, FormSelect, FormRadioHorizontal, FormDate, ComponentTitle, PopNotify, FormNumber, FormCheckbox, FormText },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  computed: {
    keyName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetName', value)
      }
    },
    keyCode: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyCode
      }
    },
    keyLink: {
      get () {
        return document.location.origin + this.$router.resolve({ name: 'auth-validate-key' }).href + `?registerKey=` + this.keyCode
      }
    },
    keyLimit: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyUsageLimit
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetUsageLimit', value)
      }
    },
    keyUsed: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyUsed
      }
    },
    keyExpiryDate: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyExpiryDate
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetExpiryDate', value)
      }
    },
    keyOrganisationUnitsAvailableFor: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyOrgUnitsAvailableFor
      },
      set (value) {
        this.$refs.selectorgunits.clearSelected()
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetOrgUnitsAvailableFor', value)
      }
    },
    keyOrganisationUnitsList: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyOrgUnitsList
      }
    },
    keyGroupsAvailableFor: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyGroupsAvailableFor
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetGroupsAvailableFor', value)
      }
    },
    keyGroupsList: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyGroupsList
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceKeySetGroupsList', value)
      }
    },
    keyStatus: {
      get () {
        const rawValue = this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyStatus
        return rawValue === 'active'
      }
    },
    isAddMode () {
      return this.$route.name.includes('workspace-add')
    },
    isEditMode () {
      return this.$route.name.includes('workspace-edit')
    },
    todaysDate () {
      return toISODateUTC(new Date())
    },
    originWorkspaceId () {
      return this.$route.params.workspaceId
    },
    routeToOriginWorkspace () {
      return { name: 'lms-superadmin-workspace-edit-self-registration', params: { id: this.originWorkspaceId } }
    },
    orgUnitsLength () {
      return this.$store.getters.getLmsSuperadminOrgUnitsMappedLabelValue.length
    },
    groupsLength () {
      return this.$store.getters.getLmsSuperadminGroupsMappedLabelValue.length
    }
  },
  methods: {
    setKeyOrganisationUnitsList (value) {
      this.$store.dispatch('lmsSuperadminWorkspaceKeySetOrgUnitsList', value)
    },
    setKeyStatus (value) {
      this.$store.dispatch('lmsSuperadminWorkspaceKeySetStatus', value === false ? 'inactive' : 'active')
    },
    redirectToWorkspaceOrigin () {
      this.$router.push(this.routeToOriginWorkspace)
    },
    performSubmitValidateThenCallback () {
      this.redirectToWorkspaceOrigin()
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditselfregistrationeditform.scss";
</style>
